import React, {useCallback, useMemo, useRef, useState} from "react";
import {ProductsDataGridViewColumnNames} from "../../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../../containers/data-grid";
import useAccessLevels from "../../../../../../../hooks/use-access-levels";
import Slider from "react-slick";
import LazyImage from "../../../../../../../components/app-specific/lazy-image";
import classnames from "classnames";
import {Box, Button, Card, CircularProgress, IconButton, SvgIcon, Tooltip, Typography} from "@mui/material";
import {AutorenewRounded, OpenInNew} from "@mui/icons-material";
import {ReactComponent as CompareOutLinedIcon} from "../../../../../../../../assets/images/compare-outlined.svg";
import ProductTagsContainer from "../../../../../../../components/app-specific/lookup-product-card/product-tags-container";
import {Link} from "react-router-dom";
import {ProductTitleText} from "../../../../products/single";
import {productInformationPopoverTabs} from "../../../../../../../components/app-specific/draggable-popover/product-information-popover/product-information";
import DeliveryModeChip from "../../../../../../../components/app-specific/delivery-mode-chip";
import VariantOptionPickers from "../../../../../../../components/app-specific/variant-option-pickers";
import {useTranslation} from "react-i18next";

const dataGridSaveConfig = {
    name: 'LookupSearchViewProductsDataGrid',
    version: 2,
}

const ProductsDataGrid = ({
                              readonly,
                              mainProducts,
                              relevantProducts,
                              canGetExtraInfo,
                              inProductsList,
                              loading,
                              hasMoreToLoad,
                              loadMoreData,
                              paginationInfo,
                              comparing,
                              inCompareList,
                              gettingExtraInfo,
                              toggleInCompareList,
                              onGetExtraInfoClicked,
                              onProductClicked,
                              selectedVariety,
                              onVarianceClicked,
                              varianceProperties = [],
                              onAggregatedSelectionChange,
                          }) => {
    const operatorAccessLevels = useAccessLevels();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.lookup-v2-search-products'});

    const allProducts = useMemo(() => [...((mainProducts ?? [])), ...((relevantProducts ?? []))], [mainProducts, relevantProducts]);

    const canViewNavigation = useMemo(() =>
            (operatorAccessLevels.packages.lookup.lookupProductInformation && !inProductsList) ||
            (operatorAccessLevels.packages.lookup.productInformation && inProductsList),
        [inProductsList, operatorAccessLevels.packages.lookup.lookupProductInformation, operatorAccessLevels.packages.lookup.productInformation]);

    const canViewGetExtraInfo = useMemo(() => !!canGetExtraInfo, [canGetExtraInfo]);

    const canViewComparisonList = useMemo(() => operatorAccessLevels.packages.lookup.comparisonList, [operatorAccessLevels.packages.lookup.comparisonList]);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: ProductsDataGridViewColumnNames.actions,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                pinned: true,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: [canViewNavigation, canViewGetExtraInfo, canViewComparisonList].filter(e => e).length * 24,
            },
            {
                title: "Product",
                name: ProductsDataGridViewColumnNames.partNumber,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: true,
                width: 150,
            },
            {
                title: "category",
                name: ProductsDataGridViewColumnNames.category,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Images",
                name: ProductsDataGridViewColumnNames.images,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: false,
                width: 200,
            },
            ...(!inProductsList ? [
                {
                    title: "Availability",
                    name: ProductsDataGridViewColumnNames.availability,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 150,
                },
            ] : []),
            {
                title: t('variantsColumn'),
                name: ProductsDataGridViewColumnNames.variants,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 240,
            },
            {
                title: "Additional Properties",
                name: ProductsDataGridViewColumnNames.additionalProperties,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 200,
            },
            {
                title: "Codes",
                name: ProductsDataGridViewColumnNames.codes,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 150,
            },
            {
                title: "Tags",
                name: ProductsDataGridViewColumnNames.tags,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 220,
            },
        ], [canViewComparisonList, canViewGetExtraInfo, canViewNavigation, inProductsList, t])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });


    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return allProducts.map((product) => {
            return ({
                key: product.info?.varietyKey ?? product.id,
                data: product,
                cells: {
                    id: product.info?.varietyKey ?? product.id,
                    [ProductsDataGridViewColumnNames.actions]:
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5}}>
                            {
                                canViewNavigation && product.link?.href ? (
                                    <IconButton
                                        color={'paper.primary'}
                                        component={Link}
                                        to={product.link.href}
                                        disabled={readonly}
                                        onClick={(event) => onProductClicked(event, product.info, product.link)}
                                    >
                                        <OpenInNew/>
                                    </IconButton>
                                ) : null
                            }
                            {
                                canViewComparisonList &&
                                <Tooltip title={inCompareList[product.info?.id] ? "Remove from compare list" : "Add to compare list"} arrow>
                                    <Box component={'span'}>
                                        <IconButton
                                            color={'paper.primary'}
                                            onClick={(event) => toggleInCompareList(event, product.info)}
                                            disabled={comparing[product.info?.id]}
                                            className={classnames({'active': inCompareList[product.info?.id], 'selected': inCompareList[product.info?.id]})}
                                            sx={{
                                                '&.active': {
                                                    backgroundColor: (theme) => theme.palette.primary.main,
                                                    border: 'unset',
                                                    '& .fill': {
                                                        fillOpacity: 1,
                                                        fill: (theme) => theme.palette.primary.contrastText,
                                                    },
                                                    '& .stroke': {
                                                        strokeOpacity: 1,
                                                        stroke: (theme) => theme.palette.primary.contrastText,
                                                    },
                                                    '& .color': {
                                                        opacity: 1,
                                                        color: (theme) => theme.palette.primary.contrastText,
                                                    },
                                                },
                                            }}
                                        >
                                            {
                                                comparing[product.info?.id]
                                                    ? <CircularProgress size={20}/>
                                                    : <SvgIcon component={CompareOutLinedIcon}
                                                               className={classnames('color', {'active': inCompareList[product.info?.id], 'selected': inCompareList[product.info?.id]})}/>
                                            }
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                            }
                            {
                                canViewGetExtraInfo && !!canGetExtraInfo ? (
                                    <Tooltip title={gettingExtraInfo[product.info?.id] ? "Fetching Availability..." : "Fetch Availability"} arrow>
                                        <Box component={'span'}>
                                            <IconButton
                                                color={'paper.primary'}
                                                onClick={(event) => onGetExtraInfoClicked(event, product.info)}
                                                disabled={gettingExtraInfo[product.info?.id]}
                                            >
                                                {
                                                    gettingExtraInfo[product.info?.id]
                                                        ? <CircularProgress size={20}/>
                                                        : <AutorenewRounded className={'color'}/>
                                                }
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                ) : null
                            }
                        </Box>,
                    [ProductsDataGridViewColumnNames.images]: (
                        <ImagesSection info={product.info}/>
                    ),
                    [ProductsDataGridViewColumnNames.partNumber]: (
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, py: 0.5}}>
                            <ProductTitleText
                                title={product.info?.title ?? ""}
                                partNo={product.info?.partNo}
                                partNoPopoverProps={{
                                    startingTab: productInformationPopoverTabs.technicalInfo,
                                }}
                            />
                            {
                                !!product.info.variety && !!onVarianceClicked && operatorAccessLevels.packages.lookup.productVariants &&
                                <div className={'product-header-line'}>
                                    <Button
                                        variant={'text'}
                                        color={'secondary'}
                                        onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onVarianceClicked(product.info);
                                        }}
                                    >
                                        {selectedVariety === product.info.variety ? "" : "More Like This"}
                                    </Button>
                                </div>
                            }
                        </Box>
                    ),
                    [ProductsDataGridViewColumnNames.category]:
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                            <Box sx={{mt: 0.25}}>
                                <DeliveryModeChip deliveryMode={product.info?.deliveryMode} size={'small'}/>
                            </Box>
                            {`${product.info?.rootCategoryName ?? ""}${product.info?.rootCategoryName && product.info?.leafCategoryName ? " > " : ""}${product.info?.leafCategoryName ?? ""}`}
                        </Box>,
                    [ProductsDataGridViewColumnNames.availability]: (
                        <AvailabilitySection availabilityInfo={product.availabilityInfo}/>
                    ),
                    [ProductsDataGridViewColumnNames.variants]: (
                        product.info?.isAggregated
                            ? (
                                <VariantOptionPickers
                                    info={product.info}
                                    varianceProperties={varianceProperties}
                                    onAggregatedSelectionChange={onAggregatedSelectionChange}
                                    compact
                                />
                            )
                            : (
                                <Typography variant={'body2'} color={'text.secondary'}>—</Typography>
                            )
                    ),
                    [ProductsDataGridViewColumnNames.additionalProperties]: (
                        <AdditionalPropertiesSection additionalProperties={product.additionalProperties}/>
                    ),
                    [ProductsDataGridViewColumnNames.codes]: (
                        <CodesSection info={product.info}/>
                    ),
                    [ProductsDataGridViewColumnNames.tags]: (
                        <TagsSection info={product.info}/>
                    ),
                },
            })
        });
    }, [allProducts, canGetExtraInfo, canViewComparisonList, canViewGetExtraInfo, canViewNavigation, comparing, gettingExtraInfo, inCompareList, onAggregatedSelectionChange, onGetExtraInfoClicked, onProductClicked, onVarianceClicked, operatorAccessLevels.packages.lookup.productVariants, readonly, selectedVariety, toggleInCompareList, varianceProperties])

    const onCurrentPageChanged = useCallback((_, __, change) => {
        if (loading || !hasMoreToLoad)
            return;
        loadMoreData(change)
    }, [hasMoreToLoad, loadMoreData, loading]);

    return (
        <>
            {
                !!allProducts.length &&
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    columns={dataGridColumns}
                    classNames={{
                        container: 'pt-2',
                    }}
                    initialLayoutState={{columns: columns}}
                    hideFooter
                    hideToolbar
                    useInfiniteScroll
                    minRowHeight={130}
                    infiniteScrollHasMoreData={hasMoreToLoad}
                    infiniteScrollGetScrollParent={() => document.querySelector('main')}
                    visibleRows={allProducts.length}
                    rows={dataGridRows}
                    state={{loading: {state: loading}, density: dataGridDensity, pagination: paginationInfo}}
                    onCurrentPageChanged={onCurrentPageChanged}
                />
            }
        </>
    );
}

export default ProductsDataGrid;


const ImagesSection = ({info}) => {
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const sliderRef = useRef(null);

    const images = useMemo(() => !info?.images?.length ? [null] : info?.images, [info?.images])

    /**
     * After each change in the slide, sets the [activeSlide] to the received number
     * @param index
     */
    const afterSlideChange = useCallback((index) => setActiveImageIndex(index), []);

    return (
        <>
            <div
                style={{height: 120, width: '100%', cursor: 'initial'}}
            >
                <div style={{height: 'inherit'}}>
                    <Box
                        className={'product-images'}
                        sx={{width: '100%'}}
                        onDragStart={e => e.preventDefault() || e.stopPropagation()}
                        onMouseDown={e => e.preventDefault() || e.stopPropagation()}
                        onTouchStart={e => e.preventDefault() || e.stopPropagation()}
                    >
                        <Slider
                            autoplay={false}
                            infinite={true}
                            arrows={false}
                            initialSlide={0}
                            afterChange={afterSlideChange}
                            slidesToShow={1}
                            ref={sliderRef}
                        >
                            {
                                images.map((image) => (
                                    <div key={image ?? 'placeholder'} className="img">
                                        <LazyImage
                                            src={image}
                                            alt={image}
                                            height={100}
                                            onContextMenu={(e) => e.preventDefault()}
                                            onDragStart={(e) => e.preventDefault()}
                                            className={'disable-downloading-image'}
                                            style={{
                                                borderRadius: 8,
                                                maxWidth: '100%',
                                            }}
                                        />
                                    </div>
                                ))
                            }
                        </Slider>
                        <div className={'lookup-product-images-data-grid-custom-dots'}>
                            {
                                images.map((item, index) => (
                                    <div
                                        key={item ?? 'placeholder'}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            sliderRef.current?.slickGoTo(index);
                                        }}
                                        className={classnames(
                                            'dot',
                                            {
                                                'active': index === activeImageIndex,
                                                'active-minus-one': (index === activeImageIndex + 1) || (index === activeImageIndex - 1),
                                                'active-minus-two': (index === activeImageIndex + 2) || (index === activeImageIndex - 2),
                                            }
                                        )}/>
                                ))
                            }
                        </div>
                    </Box>
                </div>
            </div>
        </>
    );
}

const AvailabilitySection = ({availabilityInfo}) => {
    const availability = useMemo(() => availabilityInfo?.filter(e => e._has) ?? [], [availabilityInfo]);

    return (
        <>
            {
                (!!availability?.length) &&
                <div className={'d-flex flex-wrap w-100 mt-2'} style={{gap: 8}}>
                    {
                        availability.map((availability) => (
                            <Tooltip
                                key={availability.warehouse}
                                title={`Availability of ${availability.fullName} from ${availability.partner}: ${availability.availability}`}
                                arrow
                            >
                                <Card
                                    variant={'elevation'}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        boxShadow: 'none',
                                        backgroundColor: 'secondary.light',
                                        borderRadius: 1,
                                        paddingInline: 1,
                                        paddingBlock: 0.5,
                                    }}
                                >
                                    <Box sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '100%',
                                        backgroundColor: availability.availabilityColorCode,
                                    }}/>
                                    <Typography variant={'caption'} fontWeight={600}>
                                        {availability.warehouse}
                                        <Typography
                                            component={'span'}
                                            sx={{display: 'inline-block', marginInlineStart: 0.5}}
                                            variant={'body2'}
                                            fontWeight={600}
                                            color={'secondary'}
                                        >
                                            ({availability.availability})
                                        </Typography>
                                    </Typography>
                                </Card>
                            </Tooltip>
                        ))
                    }
                </div>
            }
        </>
    );
}

const AdditionalPropertiesSection = ({additionalProperties}) => {

    return (
        <>

            {
                (!!additionalProperties?.length) &&
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexWrap: 'wrap',
                        alignContent: 'flex-start',
                        gap: 1,
                        width: '100%',
                        mt: 1,
                        // ~2 property chip rows; overflow scrolls sideways
                        maxHeight: 80,
                        pb: 1.5,
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        scrollbarWidth: 'thin',
                        boxSizing: 'border-box',
                    }}
                >
                    {
                        additionalProperties?.map((property) => (
                            <Tooltip
                                key={`${property.title}-${property.value}`}
                                title={`${property.title}: ${property.tooltipValue ?? property.displayValue}`}
                                arrow
                            >
                                <Card
                                    variant={'elevation'}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        flexShrink: 0,
                                        boxShadow: 'none',
                                        backgroundColor: 'secondary.light',
                                        borderRadius: 1,
                                        paddingInline: 1,
                                        paddingBlock: 0.5,
                                    }}
                                >
                                    <Typography variant={'caption'} fontWeight={600}>
                                        {property.title}:
                                        <Typography
                                            component={'span'}
                                            sx={{display: 'inline-block', marginInlineStart: 0.5}}
                                            variant={'body2'}
                                            fontWeight={600}
                                            color={'secondary'}
                                        >
                                            {property.displayValue}
                                        </Typography>
                                    </Typography>
                                </Card>
                            </Tooltip>
                        ))
                    }
                </Box>
            }
        </>
    );
}

const TagsSection = ({info}) => {

    return (
        <>
            {
                !!info?.tags?.length &&
                <ProductTagsContainer
                    tags={info?.tags}
                />
            }
        </>
    )
}

const CodesSection = ({info}) => {
    const codeList = useMemo(() => [...new Set((info?.codes) ?? [])], [info?.codes])
    return (
        <>
            {
                codeList.length > 0 &&
                <div className={'product-codes mt-2'}>
                    {
                        codeList.map(code => (
                            <p key={code}>
                                {code ?? ''}
                            </p>
                        ))
                    }
                </div>
            }
        </>
    );
}